import { useCallback, useRef, useState } from "react";
import { streamChatMessage } from "../services/askDostService";
import { ISendChatMessage, ISSEEvent } from "../types";

interface IUseChatStreamProps {
  onEvent: (event: ISSEEvent) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
}

export const useChatStream = ({
  onEvent,
  onComplete,
  onError,
}: IUseChatStreamProps) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const streamChat = useCallback(
    async (data: ISendChatMessage) => {
      setIsStreaming(true);
      abortControllerRef.current = new AbortController();

      try {
        const response = await streamChatMessage(data);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error("Response body is not readable");
        }

        let buffer = "";
        let done = false;

        while (!done) {
          if (abortControllerRef.current?.signal.aborted) {
            reader.cancel();
            break;
          }

          const result = await reader.read();
          done = result.done;
          buffer += decoder.decode(result.value ?? new Uint8Array(), {
            stream: !done,
          });

          const lines = buffer.split("\n");
          buffer = done ? "" : lines.pop() ?? "";

          for (const line of lines) {
            if (processSSELine(line.trim(), onEvent)) {
              onComplete();
              return;
            }
          }
        }

        onComplete();
      } catch (error) {
        console.error("Stream error:", error);
        if (error instanceof Error && error.name !== "AbortError") {
          onError(error);
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [onEvent, onComplete, onError],
  );

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return { streamChat, isStreaming, cancelStream };
};

// Returns true when the stream should be treated as complete (a "done" event).
function processSSELine(
  line: string,
  onEvent: (event: ISSEEvent) => void,
): boolean {
  // Heartbeats are comment lines per the SSE spec and carry no payload.
  if (!line || line.startsWith(":")) return false;

  if (!line.startsWith("data:")) return false;

  const jsonStr = line.slice("data:".length).trim();
  if (!jsonStr) return false;

  try {
    const event = JSON.parse(jsonStr) as ISSEEvent;
    if (!event?.type) return false;

    onEvent(event);
    return event.type === "done";
  } catch (parseError) {
    console.error("Failed to parse SSE data:", parseError);
    return false;
  }
}
